import { lireTout, ecrire, supprimer, neuf, touche } from './base.js';

// STATUTS et filtrer vivent dans src/devis.js : ce sont du domaine, sans
// entrée-sortie, donc testables sans IndexedDB ni installation de paquets.

/**
 * Accès aux devis - document 02, section 3.
 *
 * **Un devis est immuable.** Une fois créé, ses champs de calcul ne changent
 * jamais : seuls `note`, `status`, `updatedAt` et `deleted` évoluent. C'est ce
 * qui garantit qu'un prix annoncé à un client reste traçable tel quel, même
 * après un changement de paramètres ou une évolution du moteur.
 *
 * Cette couche est le seul endroit qui écrit dans le magasin `quotes` : c'est
 * elle qui rend l'immuabilité vérifiable, plutôt qu'une convention que chaque
 * appelant devrait respecter.
 */

/** Champs du profil recopiés dans le devis - document 02, section 3. */
const CHAMPS_SNAPSHOT = [
  'name', 'cfaCurrency', 'officialRate', 'rateOut', 'rateIn',
  'serviceFixed', 'servicePercent', 'operatorPercent', 'operatorFixed', 'operatorCap',
];

export async function listerDevis() {
  const liste = await lireTout('quotes');
  // Chronologique inverse : le plus récent d'abord (document 04).
  return liste.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * Fige une cotation en devis.
 *
 * Le profil est **recopié**, pas référencé : un profil modifié plus tard ne
 * doit pas réécrire l'histoire. Le résultat du moteur est stocké tel quel pour
 * la même raison - on ne recalcule jamais un devis à l'affichage.
 */
export async function enregistrerDevis({ profil, params, direction, basis, feeMode, inputAmount, note, result }) {
  const profileSnapshot = Object.fromEntries(
    CHAMPS_SNAPSHOT.map((c) => [c, c === 'name' || c === 'cfaCurrency' ? profil?.[c] : params[c] ?? profil?.[c]])
  );

  const devis = neuf({
    note: note ?? '',
    status: 'brouillon',
    profileSnapshot,
    profileId: profil?.id ?? null,
    direction,
    basis,
    feeMode,
    inputAmount,
    result,
  });

  await ecrire('quotes', devis);
  return devis;
}

/**
 * Seules deux évolutions sont permises. La signature n'accepte volontairement
 * rien d'autre : passer par un objet libre ouvrirait la porte à une
 * modification de montant, et l'immuabilité ne serait plus qu'une intention.
 */
export async function majDevis(devis, { note, status }) {
  const modifie = touche({
    ...devis,
    ...(note !== undefined ? { note } : {}),
    ...(status !== undefined ? { status } : {}),
  });
  await ecrire('quotes', modifie);
  return modifie;
}

export const supprimerDevis = (id) => supprimer('quotes', id);
